import apiClient from '../lib/apiClient';

const clientRequirementsService = {
  // Get all client requirements with optional filters
  getRequirements: async (filters = {}) => {
    try {
      const result = await apiClient.get('/requirements', filters);
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to fetch client requirements' };
    }
  },

  // Get requirement by ID
  getRequirement: async (requirementId) => {
    try {
      const result = await apiClient.get(`/requirements/${requirementId}`);
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to fetch requirement details' };
    }
  },

  // Get all clients
  getClients: async () => {
    try {
      const result = await apiClient.get('/requirements/clients');
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to fetch clients' };
    }
  },

  // Create new client requirement
  createRequirement: async (requirementData) => {
    try {
      const result = await apiClient.post('/requirements', requirementData);
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to create client requirement' };
    }
  },

  // Update existing client requirement
  updateRequirement: async (requirementId, requirementData) => {
    try {
      const result = await apiClient.put(`/requirements/${requirementId}`, requirementData);
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to update client requirement' };
    }
  },

  // Get matching candidates for a requirement
  getMatchingCandidates: async (requirementId) => {
    try {
      const result = await apiClient.get(`/requirements/${requirementId}/candidates`);
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to fetch matching candidates' };
    }
  },

  // Create candidate match
  createCandidateMatch: async (matchData) => {
    try {
      const result = await apiClient.post('/requirements/createMatch', matchData);
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to create candidate match' };
    }
  },

  // Update candidate match status
  updateMatchStatus: async (matchId, status, notes = '') => {
    try {
      const result = await apiClient.post('/requirements/updateMatchStatus', {
        match_id: matchId,
        status: status,
        notes: notes
      });
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to update match status' };
    }
  },

  // Get requirements analytics
  getRequirementsAnalytics: async () => {
    try {
      const result = await apiClient.get('/requirements/analytics');
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to fetch requirements analytics' };
    }
  },

  // Bulk update requirements
  bulkUpdateRequirements: async (requirementIds, updateData) => {
    try {
      const result = await apiClient.post('/requirements/bulkUpdate', {
        requirement_ids: requirementIds,
        ...updateData
      });
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to bulk update requirements' };
    }
  },

  // Update client response for a lead submission
  updateClientResponse: async (submissionId, response, notes = '') => {
    try {
      const result = await apiClient.post('/crm/updateClientResponse', {
        submission_id: submissionId,
        response: response,
        notes: notes
      });
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to update client response' };
    }
  },

  // Export requirements as CSV (triggers browser download)
  exportRequirements: async (filters = {}) => {
    try {
      const token = apiClient.getToken();
      const params = new URLSearchParams({ ...filters, token });
      const url = `${apiClient.baseURL}/requirements/export?${params.toString()}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) {
        const text = await response.text();
        let msg = 'Export failed';
        try { msg = JSON.parse(text).error || msg; } catch (_) {}
        return { success: false, error: msg };
      }
      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^";\n]+)"?/);
      const filename = match ? match[1] : `client_requirements_${new Date().toISOString().slice(0,10)}.csv`;
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message || 'Failed to export requirements' };
    }
  },

  // Download blank import template CSV
  downloadImportTemplate: async () => {
    try {
      const token = apiClient.getToken();
      const url = `${apiClient.baseURL}/requirements/import-template?token=${token}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) {
        return { success: false, error: 'Failed to download template' };
      }
      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = 'client_requirements_import_template.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message || 'Failed to download template' };
    }
  },

  // Import requirements from CSV file
  importRequirements: async (file) => {
    try {
      const token = apiClient.getToken();
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${apiClient.baseURL}/requirements/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await response.json();
      return { success: data.success, data: data.data, error: data.error, message: data.message };
    } catch (error) {
      return { success: false, error: error.message || 'Failed to import requirements' };
    }
  }
};

export default clientRequirementsService;
